"use strict";
/**
 * Account-relative colour anchors for Creative Performance.
 *
 * One rule: green = better than the account's own 90-day average, yellow = average, red = worse.
 * Strength tracks distance from average on the account's own scale (p5↔p95).
 *
 * W = Σnumerator / Σdenominator over eligible ads (account aggregate — not spend-weighted mean).
 * G = full green edge (p95 higher-better, p5 lower-better).
 * R = full red edge (p5 higher-better, p95 lower-better).
 * W is pinned inside [R, G] so the client mix never divides by a flipped span.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.NEGATIVE_EVIDENCE_CPR_MULTIPLE = exports.CREATIVE_SUFFICIENCY_EVENTS = exports.CREATIVE_SUFFICIENCY_IMPRESSIONS = exports.ACCOUNT_LEARNING_EVENT_FLOOR = exports.METRIC_ANCHOR_WINDOW_DAYS = exports.METRIC_ANCHOR_MIN_ADS = exports.CREATIVE_METRIC_RAMP_DIRECTION = void 0;
exports.evaluateLimitedData = evaluateLimitedData;
exports.pinAnchorEdges = pinAnchorEdges;
exports.metricRampT = metricRampT;
exports.isGradientColorCoding = isGradientColorCoding;
/**
 * Metrics that colour against account W/G/R when anchors ship on the CP response.
 *
 * Video-only keys stay listed here; the server scopes their ads to video delivery and the client
 * withholds the tint on statics. Cost-per-result is gated per-row (terminal event + not mixed).
 */
exports.CREATIVE_METRIC_RAMP_DIRECTION = {
    roas: 'higher',
    cpa: 'lower',
    costPerResult: 'lower',
    outboundCtr: 'higher',
    costPerOutboundClick: 'lower',
    cpm: 'lower',
    landingPageViewRate: 'higher',
    costPerLandingPageView: 'lower',
    addToCartRate: 'higher',
    costPerAddToCart: 'lower',
    conversionRate: 'higher',
    revenuePerVisitor: 'higher',
    hookRate: 'higher',
    holdRate: 'higher',
    hookToClick: 'higher',
    firstFrameRetention: 'higher',
    retentionP100: 'higher',
    costPerThruPlay: 'lower',
    engagementRate: 'higher',
    costPerEngagement: 'lower',
    costPerLeads: 'lower',
    clickToPurchase: 'higher',
    checkoutCompletionRate: 'higher',
    cartToPurchase: 'higher',
};
/** Thin accounts: below this many eligible ads, withhold that metric's ramp. */
exports.METRIC_ANCHOR_MIN_ADS = 10;
/** Rolling window the anchors and the sufficiency gate share. */
exports.METRIC_ANCHOR_WINDOW_DAYS = 90;
/** Account-level learning floor — below this, nothing on the surface is coloured. */
exports.ACCOUNT_LEARNING_EVENT_FLOOR = 5;
/** Creative impressions floor for the universal sufficiency gate. */
exports.CREATIVE_SUFFICIENCY_IMPRESSIONS = 1000;
/** Creative relevant-event floor for the universal sufficiency gate. */
exports.CREATIVE_SUFFICIENCY_EVENTS = 5;
/** Spend / CPR multiple that counts as enough negative evidence. */
exports.NEGATIVE_EVIDENCE_CPR_MULTIPLE = 3;
/**
 * One gate decides whether an ad takes part in anchors and whether a row's value may be coloured.
 * Account prerequisite and cohort membership use the trailing 90d window; the selected range only
 * gates the row value being coloured.
 */
function evaluateLimitedData(args) {
    if (args.accountRelevantEvents < exports.ACCOUNT_LEARNING_EVENT_FLOOR)
        return 'learning';
    const ref = args.referenceCostPerResult;
    const negativeEvidence = ref !== null &&
        Number.isFinite(ref) &&
        ref > 0 &&
        ((args.creativeRelevantEvents === 0 && args.creativeSpend >= exports.NEGATIVE_EVIDENCE_CPR_MULTIPLE * ref) ||
            (args.creativeRelevantEvents >= 3 &&
                args.creativeCostPerResult !== null &&
                Number.isFinite(args.creativeCostPerResult) &&
                args.creativeCostPerResult >= exports.NEGATIVE_EVIDENCE_CPR_MULTIPLE * ref));
    const sufficient = args.creativeImpressions >= exports.CREATIVE_SUFFICIENCY_IMPRESSIONS ||
        args.creativeRelevantEvents >= exports.CREATIVE_SUFFICIENCY_EVENTS ||
        negativeEvidence;
    return sufficient ? 'sufficient_data' : 'limited_data';
}
/**
 * Pin W inside [R, G] for the metric's direction so the client mix never sees a flipped span.
 * Values beyond G/R still clamp to full green / full red.
 */
function pinAnchorEdges(args) {
    const { w, direction } = args;
    let { g, r } = args;
    if (direction === 'higher') {
        g = Math.max(g, w);
        r = Math.min(r, w);
    }
    else {
        g = Math.min(g, w);
        r = Math.max(r, w);
    }
    return { w, g, r };
}
/**
 * Signed tint in [-1, 1]: 0 at average (W), +1 at full green (G), −1 at full red (R).
 *
 * Uses the sign of `(value - W) / (G - W)` so both directions work after G/R swap sides for costs
 * (the brief's `value >= W` form only holds when G > W).
 */
function metricRampT(value, anchor) {
    const { w, g, r } = anchor;
    if (!Number.isFinite(value) || !Number.isFinite(w))
        return 0;
    if (value === w)
        return 0;
    const greenSpan = g - w;
    const redSpan = r - w;
    // A pinned edge may coincide with the average. The opposite edge still establishes direction.
    if (greenSpan === 0 && redSpan !== 0 && (value - w) / redSpan < 0)
        return 1;
    if (redSpan === 0 && greenSpan !== 0 && (value - w) / greenSpan < 0)
        return -1;
    if (Math.abs(greenSpan) > 0) {
        const towardGreen = (value - w) / greenSpan;
        if (towardGreen >= 0)
            return clamp01(towardGreen);
    }
    if (!(Math.abs(redSpan) > 0))
        return 0;
    return -clamp01((value - w) / redSpan);
}
function clamp01(t) {
    if (t <= 0)
        return 0;
    if (t >= 1)
        return 1;
    return t;
}
/** Whether a catalog / CSV `color_coding` cell means "ship the gradient" for this product cut. */
function isGradientColorCoding(colorCoding) {
    const v = colorCoding.trim().toLowerCase();
    if (!v.startsWith('gradient'))
        return false;
    if (v.includes('(later)'))
        return false;
    return true;
}
